<script setup>
import BackButton from '~/components/back-button/back-button.vue';
import { computed, ref } from '#imports';
import SearchForm from '~/components/search-form/search-form.vue';
import NewsHighlight from '~/components/news-page/news-highlight/news-highlight.vue';

const props = defineProps({
  headerText: { type: String, default: null },
  withBackButton: { type: Boolean, default: false },
  searchQuery: { type: String, default: null },
  withSearch: { type: Boolean, default: false },
  showSvgTitle: { type: Boolean, default: false },
  highlightNewsData: { type: Object, default: null },
});

const getNewsRoute = (slug) => ({
  name: 'news-slug',
  params: { slug: slug.current },
});

const highlightCard = computed(() => props.highlightNewsData[0]);
const highlightSlug = computed(() => highlightCard.value.card.slug);
const highlightTitle = computed(() => highlightCard.value.card.title);

const emit = defineEmits(['back', 'search']);
const searchInput = ref();

const handleResetSearch = () => {
  searchInput.value = '';
};

const handleGoBack = () => {
  emit('back');
};

const handlePerformSearch = (value) => {
  emit('search', value);
};
</script>

<template>
  <div class="news-page-header">
    <div class="news-page-header__title">
      <BackButton v-if="withBackButton" @back="handleGoBack" />
      <div v-if="!withSearch">
        <h2 v-if="headerText">{{ props.headerText }}</h2>
        <div v-if="showSvgTitle" class="news-page-header__svg">
          <svg
            width="635"
            height="89"
            viewBox="0 0 635 89"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.540001 -6.91414e-06H27.568V87H0.540001V-6.91414e-06ZM68.284 -6.91414e-06H95.312V87H68.284V-6.91414e-06ZM15.736 30.74H79.188V54.636H15.736V30.74ZM148.15 88.972C139.566 88.972 132.026 87.5027 125.53 84.564C119.034 81.6253 113.969 77.5267 110.334 72.268C106.699 66.932 104.882 60.784 104.882 53.824C104.882 46.7093 106.699 40.5227 110.334 35.264C113.969 29.928 119.034 25.8293 125.53 22.968C132.026 20.0293 139.605 18.56 148.266 18.56C156.85 18.56 164.39 20.0293 170.886 22.968C177.382 25.8293 182.409 29.928 185.966 35.264C189.601 40.5227 191.418 46.7093 191.418 53.824C191.418 60.784 189.601 66.932 185.966 72.268C182.409 77.5267 177.382 81.6253 170.886 84.564C164.39 87.5027 156.811 88.972 148.15 88.972ZM148.266 69.484C151.746 69.484 154.685 68.8653 157.082 67.628C159.557 66.3907 161.413 64.612 162.65 62.292C163.965 59.8947 164.622 57.072 164.622 53.824C164.622 50.4987 163.965 47.676 162.65 45.356C161.413 42.9587 159.557 41.1413 157.082 39.904C154.685 38.6667 151.746 38.048 148.266 38.048C144.786 38.048 141.809 38.6667 139.334 39.904C136.859 41.1413 134.965 42.9587 133.65 45.356C132.413 47.676 131.794 50.46 131.794 53.708C131.794 57.0333 132.413 59.8947 133.65 62.292C134.965 64.612 136.859 66.3907 139.334 67.628C141.809 68.8653 144.786 69.484 148.266 69.484ZM198.369 87L202.661 53.708L198.369 20.532H254.397C260.816 20.532 265.958 22.04 269.825 25.056C273.692 27.9947 275.625 32.016 275.625 37.12C275.625 41.9147 273.653 45.82 269.709 48.836C265.842 51.852 260.468 53.708 253.585 54.404L255.209 50.344C263.174 50.7307 269.245 52.432 273.421 55.448C277.597 58.464 279.685 62.64 279.685 67.976C279.685 73.776 277.52 78.416 273.189 81.896C268.858 85.2987 263.097 87 255.905 87H198.369ZM223.889 81.664L213.681 70.876H246.625C248.713 70.876 250.298 70.412 251.381 69.484C252.464 68.4787 253.005 67.1253 253.005 65.424C253.005 63.7227 252.425 62.3693 251.265 61.364C250.105 60.3587 248.365 59.856 246.045 59.856H218.785V46.748H243.145C245.078 46.748 246.548 46.284 247.553 45.356C248.558 44.428 249.061 43.1907 249.061 41.644C249.061 40.0973 248.558 38.8987 247.553 38.048C246.625 37.12 245.233 36.656 243.377 36.656H213.797L223.889 25.868L227.833 53.708L223.889 81.664ZM328.72 88.972C320.136 88.972 312.596 87.5027 306.1 84.564C299.604 81.6253 294.539 77.5267 290.904 72.268C287.27 66.932 285.452 60.784 285.452 53.824C285.452 46.7093 287.27 40.5227 290.904 35.264C294.539 29.928 299.604 25.8293 306.1 22.968C312.596 20.0293 320.175 18.56 328.836 18.56C337.42 18.56 344.96 20.0293 351.456 22.968C357.952 25.8293 362.979 29.928 366.536 35.264C370.171 40.5227 371.988 46.7093 371.988 53.824C371.988 60.784 370.171 66.932 366.536 72.268C362.979 77.5267 357.952 81.6253 351.456 84.564C344.96 87.5027 337.382 88.972 328.72 88.972ZM328.836 69.484C332.316 69.484 335.255 68.8653 337.652 67.628C340.127 66.3907 341.983 64.612 343.22 62.292C344.535 59.8947 345.192 57.072 345.192 53.824C345.192 50.4987 344.535 47.676 343.22 45.356C341.983 42.9587 340.127 41.1413 337.652 39.904C335.255 38.6667 332.316 38.048 328.836 38.048C325.356 38.048 322.379 38.6667 319.904 39.904C317.43 41.1413 315.535 42.9587 314.22 45.356C312.983 47.676 312.364 50.46 312.364 53.708C312.364 57.0333 312.983 59.8947 314.22 62.292C315.535 64.612 317.43 66.3907 319.904 67.628C322.379 68.8653 325.356 69.484 328.836 69.484ZM462.443 58.348C461.824 64.4573 459.62 69.832 455.831 74.472C452.042 79.0347 447.092 82.592 440.983 85.144C434.951 87.696 428.146 88.972 420.567 88.972C412.292 88.972 404.946 87.5027 398.527 84.564C392.186 81.6253 387.236 77.488 383.679 72.152C380.122 66.816 378.343 60.7067 378.343 53.824C378.343 46.7867 380.122 40.6387 383.679 35.38C387.236 30.1213 392.186 26.0227 398.527 23.084C404.946 20.068 412.292 18.56 420.567 18.56C428.146 18.56 434.951 19.836 440.983 22.388C447.092 24.94 452.042 28.4973 455.831 33.06C459.62 37.6227 461.824 42.9973 462.443 49.184H436.111C435.338 45.5493 433.559 42.804 430.775 40.948C427.991 39.0147 424.588 38.048 420.567 38.048C417.474 38.048 414.767 38.6667 412.447 39.904C410.127 41.064 408.348 42.8427 407.111 45.24C405.874 47.56 405.255 50.4213 405.255 53.824C405.255 57.2267 405.874 60.088 407.111 62.408C408.348 64.728 410.127 66.5067 412.447 67.744C414.767 68.904 417.474 69.484 420.567 69.484C424.588 69.484 427.991 68.4787 430.775 66.468C433.559 64.38 435.338 61.6733 436.111 58.348H462.443ZM489.56 29.696H516.124V87H489.56V29.696ZM465.084 20.532H540.6V40.368H465.084V20.532ZM548.22 87V20.532H574.436V72.268L569.216 70.528L600.536 20.532H634.524V87H608.424V33.524L613.528 35.38L581.28 87H548.22Z"
              fill="white"
            />
          </svg>
        </div>
      </div>

      <SearchForm
        v-if="withSearch"
        :default-value="searchQuery"
        with-button
        @search="handlePerformSearch"
        @reset="handleResetSearch"
      />
    </div>
    <div v-if="props.highlightNewsData" class="news-page-header__highlight">
      <NewsHighlight
        :news-title="highlightTitle"
        :news-slug="getNewsRoute(highlightSlug)"
      />
    </div>
  </div>
</template>

<style scoped>
.news-page-header {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 36px 0 24px 0;
  max-width: 760px;

  @media (max-width: 960px) {
    margin: 36px 0 24px 0;
    max-width: none;
  }

  &__title {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  &__svg,
  &__highlight {
    display: block;
    position: relative;
    width: 100vw;
    max-width: 675px;

    @media (max-width: 960px) {
      max-width: none;
      width: 100%;
    }

    svg {
      width: 100%;
      height: 100%;
    }
  }
}
</style>
